import { useState } from "react";
import { Calendar01Icon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ThemedDateField } from "@vega-core/components/Form/Controls/DateField/ThemedDateField";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { m } from "@vega-core/paraglide/messages";

import { composeScheduledAt } from "../DraftEmail/draftEmailUtils";
import { ScheduledTimeInput } from "../DraftEmail/ScheduledTimeInput";

type ScheduleSendPopoverProps = {
  disabled: boolean;
  isScheduling: boolean;
  onConfirm: (scheduledAt: string) => void;
};

export function ScheduleSendPopover({
  disabled,
  isScheduling,
  onConfirm,
}: ScheduleSendPopoverProps) {
  const [open, setOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
  const [scheduleTime, setScheduleTime] = useState("");

  const scheduledAt = composeScheduledAt(scheduleDate, scheduleTime);
  const isFuture =
    !!scheduledAt && new Date(scheduledAt).getTime() > Date.now();

  const handleConfirm = () => {
    if (!scheduledAt || !isFuture) return;

    onConfirm(scheduledAt);
    setOpen(false);
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <ThemedButton
          dataTestId="send-drafts-schedule"
          disabled={disabled}
          icon={<Calendar01Icon className="size-4" />}
          iconPosition="left"
          variant="secondary"
        >
          {m.coInvestments_sendDrafts_scheduleSend()}
        </ThemedButton>
      </PopoverTrigger>
      <PopoverContent align="end" className="flex w-auto flex-col gap-4">
        <span className="body-base-semibold text-text/primary">
          {m.coInvestments_sendDrafts_schedule_title()}
        </span>
        <div className="flex items-end gap-3">
          <ThemedDateField
            label={m.coInvestments_draftEmail_sendDate_label()}
            name="scheduleDate"
            onChange={setScheduleDate}
            onlyFuture
            placeholder={m.coInvestments_draftEmail_sendDate_placeholder()}
            value={scheduleDate}
          />
          <ScheduledTimeInput
            id="send-drafts-schedule-time"
            onChange={setScheduleTime}
            value={scheduleTime}
          />
        </div>
        <ThemedButton
          dataTestId="send-drafts-schedule-confirm"
          disabled={!isFuture}
          fullWidth
          icon={<Calendar01Icon className="size-4" />}
          iconPosition="left"
          isLoading={isScheduling}
          onClick={handleConfirm}
        >
          {m.coInvestments_sendDrafts_schedule_confirm()}
        </ThemedButton>
      </PopoverContent>
    </Popover>
  );
}
